import { CloseIcon, DragHandleIcon } from "@chakra-ui/icons";
import {
  Box,
  Icon,
  Image,
  Table,
  Tbody,
  Td,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { atom, useAtom } from "jotai";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { pdfRecordAtom } from "../../pages/ComparePage";
import { selectedScenariosAtom } from "../../shared/state/atoms";
import { AddScenarioModal } from "../modals/AddScenarioModal";
import {
  ResidenceTypeModel,
} from "../../clients/KompasApi";
import { ResidenceBaseBlock } from "../../components/ResidenceBaseBlock";
import { useComparePageContext } from "../../shared/state/ComparePageContext";

export const draggingColumnAtom = atom(false);

const droppedOnColumn = (
  event: React.DragEvent<HTMLDivElement>,
  ids: number[],
) => {
  let columnId = -1;
  const { pageX, pageY } = event;
  ids.forEach((id) => {
    const element = document.getElementById(`scenario-${id}`);
    if (!element) return;
    const { x, y, width, height } = element.getBoundingClientRect();
    if (pageX > x && pageX < x + width && pageY > y && pageY < y + height) {
      columnId = id;
    }
  });
  return columnId;
};

const droppedBetweenColumns = (
  event: React.DragEvent<HTMLDivElement>,
  ids: number[],
) => {
  const { pageX } = event;
  const firstColumn = document.getElementById(`scenario-${ids[0]}`);
  if (firstColumn!.getBoundingClientRect().x > pageX) return 0;
  for (let i = 1; i < ids.length; i += 1) {
    const prevElement = document.getElementById(`scenario-${ids[i - 1]}`);
    const element = document.getElementById(`scenario-${ids[i]}`);
    const { x, width } = prevElement!.getBoundingClientRect();
    if (pageX > x + width && pageX < element!.getBoundingClientRect().x) {
      return i;
    }
  }

  return 0;
};

interface Props {
  residenceType: ResidenceTypeModel;
}
export function CompareBaseTable({ residenceType }: Props) {
  const { scenarios, situation } = useComparePageContext();
  const navigate = useNavigate();
  const { onClose, isOpen, onOpen } = useDisclosure();
  const [recordingPdf] = useAtom(pdfRecordAtom);
  const [selectedScenarios, setSelectedScenarios] = useAtom(
    selectedScenariosAtom,
  );
  const [, setDraggingColumn] = useAtom(draggingColumnAtom);

  const generateRoute = useCallback(
    (scenarioIds: number[]) =>
      `/residence/${residenceType.id}/situation/${
        situation.id
      }/compare/${scenarioIds.join("&")}`,
    [residenceType.id, situation.id],
  );

  const removeScenario = useCallback(
    (scenarioId: number) => {
      const newSids = selectedScenarios.filter((id) => id !== scenarioId);
      setSelectedScenarios(newSids);
      navigate(generateRoute(newSids));
    },
    [navigate, generateRoute, selectedScenarios, setSelectedScenarios],
  );

  const updateScenarioOrder = useCallback(
    (scenarioId: number, moveRight: boolean) => {
      const newOrder = selectedScenarios.slice();
      const index = newOrder.indexOf(scenarioId);
      newOrder.splice(index, 1);
      newOrder.splice(moveRight ? index + 1 : index - 1, 0, scenarioId);
      setSelectedScenarios(newOrder);
      navigate(generateRoute(newOrder));
    },
    [navigate, generateRoute, selectedScenarios, setSelectedScenarios],
  );

  const updateScenarioOrderDragDrop = useCallback(
    (scenarioId: number, event: React.DragEvent<HTMLDivElement>) => {
      setDraggingColumn(false);
      const tableEl = document.getElementById("tables");
      if (!tableEl) return;
      const { pageX, pageY } = event;
      if (
        pageX < tableEl.offsetLeft ||
        pageX > tableEl.offsetLeft + tableEl.offsetWidth ||
        pageY < tableEl.offsetTop ||
        pageY > tableEl.offsetTop + tableEl.offsetHeight
      ) {
        return;
      }
      const swapId = droppedOnColumn(event, selectedScenarios);
      if (swapId !== -1) {
        const newOrder = selectedScenarios.slice();
        const index = newOrder.indexOf(scenarioId);
        const swapIndex = newOrder.indexOf(swapId);
        newOrder[index] = swapId;
        newOrder[swapIndex] = scenarioId;
        setSelectedScenarios(newOrder);
        navigate(generateRoute(newOrder));
        return;
      }
      const moveIndex = droppedBetweenColumns(event, selectedScenarios);
      if (moveIndex !== -1) {
        const newOrder = selectedScenarios.slice();
        const index = newOrder.indexOf(scenarioId);
        newOrder.splice(index, 1);
        newOrder.splice(
          index < moveIndex ? moveIndex - 1 : moveIndex,
          0,
          scenarioId,
        );
        setSelectedScenarios(newOrder);
        navigate(generateRoute(newOrder));
      }
    },
    [
      navigate,
      generateRoute,
      selectedScenarios,
      setDraggingColumn,
      setSelectedScenarios,
    ],
  );

  return (
    <>
      {scenarios.length < situation.scenarioCount && (
        <AddScenarioModal isOpen={isOpen} onClose={onClose} />
      )}
      <Table sx={{ borderSpacing: "2rem 0" }} variant="compare">
        <Tbody>
          <Tr className="prevent-hover">
            <Td />
            <Td
              bg="transparent !important"
              border="none !important"
              pos="relative"
            >
              <Box
                bg="black"
                borderRadius="0.25rem 0.25rem 0 0"
                color="white"
                cursor="auto"
                fontWeight="500"
                id="scenario-0"
                mx="auto"
                px={3}
                py={2}
                textAlign="center"
                w="max-content"
              >
                Huidige situatie
              </Box>
            </Td>
            {scenarios.map((scenario, i) => (
              <Td
                key={scenario.id}
                bg="transparent !important"
                border="none !important"
                pos="relative"
              >
                <Box
                  cursor="pointer"
                  display={i !== 0 ? "block" : "none"}
                  left={2}
                  onClick={() => updateScenarioOrder(scenario.id, false)}
                  pos="absolute"
                  top={2}
                >
                  <Image
                    h={4}
                    mr={2}
                    src="/svg/chevron.svg"
                    transform="rotate(270deg)"
                    w={4}
                  />
                </Box>
                <Box
                  bg="brand.gray.110"
                  borderRadius="0.25rem 0.25rem 0 0"
                  color="black"
                  cursor="grab"
                  draggable
                  fontWeight="500"
                  id={`scenario-${scenario.id}`}
                  mx="auto"
                  onDragEnd={(e) => {
                    e.preventDefault();
                    updateScenarioOrderDragDrop(scenario.id, e);
                  }}
                  onDragStart={() => {
                    setDraggingColumn(true);
                  }}
                  onDrop={(e) => e.preventDefault()}
                  px={3}
                  py={2}
                  textAlign="center"
                  w="max-content"
                >
                  <Icon as={DragHandleIcon} h={3} mr={2} mt="-0.25rem" />
                  Scenario {scenarios.indexOf(scenario) + 1}
                  {selectedScenarios.length > 1 && !recordingPdf && (
                    <CloseIcon
                      cursor="pointer"
                      h={3}
                      ml={4}
                      mt="-0.25rem"
                      onClick={() => removeScenario(scenario.id)}
                      pointerEvents="all"
                      w={3}
                    />
                  )}
                </Box>
                <Box
                  cursor="pointer"
                  display={
                    i !== selectedScenarios.length - 1 ? "block" : "none"
                  }
                  onClick={() => updateScenarioOrder(scenario.id, true)}
                  pos="absolute"
                  right={2}
                  top={2}
                >
                  <Image
                    h={4}
                    mr={2}
                    src="/svg/chevron.svg"
                    transform="rotate(90deg)"
                    w={4}
                  />
                </Box>
              </Td>
            ))}
          </Tr>
          <Tr className="prevent-hover">
            <Td />
            <Td>
              <ResidenceBaseBlock
                residenceType={residenceType}
                situation={situation}
              />
            </Td>
            {scenarios?.map((scenario) => (
              <Td key={scenario.id} borderRadius={4}>
                <ResidenceBaseBlock
                  residenceType={residenceType}
                  scenario={scenario}
                  situation={situation}
                />
              </Td>
            ))}
            {!recordingPdf &&
              selectedScenarios.length < situation.scenarioCount && (
                <Td
                  _after={{ opacity: 0 }}
                  _before={{ opacity: 0 }}
                  bg="transparent !important"
                  id="add-scenario-block"
                >
                  <Box
                    border="1px dashed"
                    borderColor="brand.grey.100"
                    borderRadius={4}
                    cursor="pointer"
                    h="380"
                    onClick={onOpen}
                  >
                    <Image
                      h={12}
                      mt="140"
                      mx="auto"
                      src="/svg/plus.svg"
                      w={12}
                    />
                    <Box textAlign="center">Scenario toevoegen</Box>
                  </Box>
                </Td>
              )}
          </Tr>
        </Tbody>
      </Table>
    </>
  );
}
